<template>
	<div>
		<div class="row">
			<!-- Available Channels -->
			<div class="col-6">
				<div class="card">
					<div class="card-header bg-light">
						<h5 class="mb-0">
							<md-icon icon="list" class="mr-2" />
							Canales Disponibles
						</h5>
					</div>
					<div class="card-body">
						<div class="d-flex align-items-center mb-3">
							<b-form-input
								v-model="searchAvailable"
								@input="onSearchAvailable"
								placeholder="Buscar canales..."
								size="sm"
								class="mr-2"
							/>
							<b-spinner v-if="searchLoading" small></b-spinner>
						</div>
						<div class="channel-list" style="max-height: 400px; overflow-y: auto;">
							<div
								v-for="channel in filteredAvailableChannels"
								:key="channel.id"
								class="channel-item d-flex justify-content-between align-items-center p-2 border rounded mb-2"
							>
								<div>
									<strong>{{ channel.name }}</strong>
									<br />
									<small class="text-muted">{{ channel.code }}</small>
								</div>
								<b-button
									@click="addChannelToGroup(channel)"
									size="sm"
									variant="success"
									title="Agregar al grupo"
								>
									<md-icon icon="add" />
								</b-button>
							</div>
							<div v-if="filteredAvailableChannels.length === 0" class="text-center text-muted py-3">
								No hay canales disponibles
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Group Channels -->
			<div class="col-6">
				<div class="card">
					<div class="card-header bg-primary text-white">
						<h5 class="mb-0">
							<md-icon icon="group" class="mr-2" />
							Canales en {{ groupName }}
						</h5>
					</div>
					<div class="card-body">
						<b-form-input
							v-model="searchGroup"
							placeholder="Buscar en el grupo..."
							size="sm"
							class="mb-3"
						/>
						<div class="channel-list" style="max-height: 400px; overflow-y: auto;">
							<div
								v-for="channel in filteredGroupChannels"
								:key="channel.id"
								class="channel-item d-flex justify-content-between align-items-center p-2 border rounded mb-2 bg-light"
							>
								<div>
									<strong>{{ channel.name }}</strong>
									<br />
									<small class="text-muted">{{ channel.code }}</small>
								</div>
								<b-button
									@click="removeChannelFromGroup(channel)"
									size="sm"
									variant="danger"
									title="Quitar del grupo"
								>
									<md-icon icon="remove" />
								</b-button>
							</div>
							<div v-if="filteredGroupChannels.length === 0" class="text-center text-muted py-3">
								No hay canales en este grupo
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Action Buttons -->
		<hr />
		<div class="text-right">
			<b-button @click="$emit('cancel')" variant="secondary">
				Cerrar
			</b-button>
		</div>
	</div>
</template>

<script>
import api from "../../../../api.js";

export default {
	props: {
		groupId: {
			type: Number,
			required: true
		},
		groupName: {
			type: String,
			required: true
		}
	},
	data() {
		return {
			allChannels: [],
			groupChannels: [],
			searchAvailable: "",
			searchGroup: "",
			loading: false,
			searchLoading: false
		};
	},
	computed: {
		availableChannels() {
			const groupChannelIds = this.groupChannels.map(c => c.id);
			return this.allChannels.filter(channel => !groupChannelIds.includes(channel.id));
		},
		filteredAvailableChannels() {
			// If searching, API handles the filtering
			// If not searching, show local filtered results
			return this.availableChannels;
		},
		filteredGroupChannels() {
			if (!this.searchGroup) return this.groupChannels;
			return this.groupChannels.filter(channel =>
				channel.name.toLowerCase().includes(this.searchGroup.toLowerCase()) ||
				channel.code.toLowerCase().includes(this.searchGroup.toLowerCase())
			);
		}
	},
	methods: {
		async loadChannels() {
			try {
				this.loading = true;
				const [allChannelsResponse, groupResponse] = await Promise.all([
					this.searchChannels(""), // Load first 100 channels
					api(this).get(`/sales-channel-groups/${this.groupId}/channels`)
				]);
				
				this.allChannels = allChannelsResponse;
				this.groupChannels = groupResponse.data.rows || [];
			} catch (error) {
				this.$root.handleRequestError(error);
			} finally {
				this.loading = false;
			}
		},
		async searchChannels(searchTerm) {
			const params = {
				limit: 100,
				offset: 0,
				sortBy: 'name',
				sortOrder: 'asc'
			};
			
			if (searchTerm) {
				params.filters = JSON.stringify({ search: searchTerm });
			}
			
			const response = await api(this).get('/available-channels-for-group', { params });
			return response.data.rows || [];
		},
		async onSearchAvailable() {
			if (!this.searchAvailable.trim()) {
				// If search is empty, reload default 100 channels
				this.searchChannels("").then(channels => {
					this.allChannels = channels;
				});
				return;
			}
			
			// Debounce search to avoid too many API calls
			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(async () => {
				try {
					this.searchLoading = true;
					const channels = await this.searchChannels(this.searchAvailable);
					this.allChannels = channels;
				} catch (error) {
					this.$root.handleRequestError(error);
				} finally {
					this.searchLoading = false;
				}
			}, 500);
		},
		async addChannelToGroup(channel) {
			try {
				await api(this).post(`/sales-channel-groups/${this.groupId}/attach-channels`, {
					salesChannelId: channel.id
				});
				
				// Add channel to group (availableChannels computed will automatically update)
				this.groupChannels.push(channel);
				
				this.$root.showAlert(`Canal "${channel.name}" agregado al grupo`, {
					variant: "success"
				});
				
			} catch (error) {
				this.$root.handleRequestError(error);
			}
		},
		async removeChannelFromGroup(channel) {
			try {
				await api(this).delete(`/sales-channel-groups/${this.groupId}/detach-channels/${channel.id}`);
				
				// Remove channel from group (availableChannels computed will automatically update)
				this.groupChannels = this.groupChannels.filter(c => c.id !== channel.id);
				
				this.$root.showAlert(`Canal "${channel.name}" removido del grupo`, {
					variant: "warning"
				});
				
			} catch (error) {
				this.$root.handleRequestError(error);
			}
		}
	},
	mounted() {
		this.loadChannels();
	},
	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
		// Emit event to refresh parent table
			this.$emit('refreshList');
	}
};
</script>

<style scoped>
.channel-item {
	transition: background-color 0.2s;
}

.channel-item:hover {
	background-color: #f8f9fa !important;
}

.card-header {
	font-weight: 500;
}

.channel-list {
	min-height: 200px;
}
</style>